import React, { Fragment, Component } from 'react';
import { createRoot } from 'react-dom/client';
import { throttle, map, pick, isEmpty } from 'lodash-es';
import cx from 'classnames';
import axios from '../../../configs/axios';

import Referral from './components/Referral';
import { AppsSelection, AppsSelectionMobile } from './components/apps-selection';
import AppSelectionMobileModal from './components/mobile-modal/AppSelectionMobileModal';
import ReportFormWrapper from './components/mobile-modal/ReportFormWrapper';
import UserReportFormMobileModal from './components/mobile-modal/UserReportFormMobileModal';
import UserReportForm from '../../../components/UserReportForm';
import RefreshRecommendations from './components/recommendation-state/RefreshRecommendations';
import ReachedQuotaState from './components/ReachedQuotaState';
import BadResponseState from './components/BadResponseState';
import SimpleCheckbox from '../../../components/Checkbox/SimpleCheckbox';
import { rootNode, initParams } from './configs';
import { scrollTo } from '../finance-free-report/utils';
import toggleSuccessUrlSearchParam from '../сompetitive-intelligence-reports/shared/utils';
import { dispatchSwitchAppFlowEvent } from '../сompetitive-intelligence-reports/shared/custom-events';
import { googleClientId, getUtmParams } from '../../../utils/ga_helpers';
import Button from '../../../components/Buttons/Button';

const scrollToTop = (offsetTop) => scrollTo(rootNode, offsetTop);

const {
  isAdminFlow,
  isMobileModalFlow,
  customMobileFields,
  customDesktopFields,

  showReportUpdatedState,
  preSelectedApp,
  topPopularApps,

  sitekey,
  sharedByReportId,
  designType,
  coverImage,
  env,
} = initParams;

const defaultSearchResults = topPopularApps.map((app) => ({
  ...app,
  value: app.name,
  kind: 'unified_app',
}));

const defaultErrors = {
  email: '',
  shareEmail: '',
};

const isMobileMode = () => {
  const mobileBreakpoint = 960;
  return window.innerWidth < mobileBreakpoint;
};

let googleId;
googleClientId().then((id) => {
  googleId = id;
});

class AppSearch extends Component {
  state = {
    mobileMode: isMobileMode(),
    selectedApps: preSelectedApp || [],
    step: 'appsSelection',
    forbidden: false,
    isSubmitting: false,
    errors: {
      email: '',
      shareEmail: '',
    },
    submitParams: {},
    skipUserStep: isAdminFlow,
    redirectedFlow: false,
    y: undefined,
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScrollPosition);
    document.addEventListener('switch-app-flow', this.handleRedirectedAppFlow);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScrollPosition);
    document.removeEventListener('switch-app-flow', this.handleRedirectedAppFlow);
  }

  handleRedirectedAppFlow = ({ detail }) => {
    this.setState({
      step: 'appsSelection',
      selectedApps: [detail.selectedApp],
      redirectedFlow: true,
    });
  };

  handleResize = throttle(() => {
    this.setState({ mobileMode: isMobileMode() });
  }, 250);

  handleScrollPosition = throttle(() => {
    if (isMobileMode()) {
      this.setState({ y: window.pageYOffset !== 0 ? window.pageYOffset : this.state.y });
    }
  }, 50);

  requestParams = (token, formParams) => {
    const { selectedApps, submitParams, skipUserStep } = this.state;
    return {
      ...submitParams,
      apps: map(selectedApps, (app) => pick(app, ['ref_no', 'store', 'name', 'similar_app'])),
      token,
      shared_by_report_id: sharedByReportId,
      skip_user: !!skipUserStep,
      bdr_generated: isAdminFlow && !skipUserStep,
      utm_url_parameters: getUtmParams(googleId),
      ...formParams,
    };
  };

  handleRedirect = (data) => {
    const shareUrl = data.report_share_url;
    const requestUrl = `/pdf_reports/${data.watchlist_id}/share`;
    window.location.assign(
      `${window.location.origin}/thank-you-report?request_url=${requestUrl}&share_url=${shareUrl}`
    );
  };

  handleFormSubmitClick = (token, formParams, errorsPresent) => {
    if (errorsPresent) {
      this.setState({ isSubmitting: false });
      return;
    }

    this.setState(
      {
        submitParams: this.requestParams(token, formParams),
        isSubmitting: true,
        errors: { ...defaultErrors },
      },
      () => {
        const { submitParams } = this.state;
        axios
          .post('/pdf_reports', submitParams)
          .then(({ data }) => {
            this.setState(
              {
                isSubmitting: false,
                forbidden: false,
                errors: { ...defaultErrors },
              },
              () => {
                if (typeof window.gtagReportConversion !== 'undefined') {
                  window.gtagReportConversion({ send_to: 'AW-972410676/pz0OCOjq9-ABELSe188D' });
                }
                this.handleRedirect(data);
              }
            );
            if (isAdminFlow) {
              setTimeout(() => {
                window.location.reload();
              }, 1500);
            } else {
              toggleSuccessUrlSearchParam('on', 'multisuccess');
            }
          })
          .catch(({ response }) => {
            if (response.status === 429) {
              this.setState({
                isSubmitting: false,
                step: 'reachedQuotaState',
                errors: { ...defaultErrors },
              });
            }
            if (response.status === 403) {
              const { ask_for_referral, email_error, share_email_error } = response.data;
              this.setState({
                isSubmitting: false,
                step: ask_for_referral ? 'showReferralState' : 'userReportForm',
                errors: {
                  email: email_error || '',
                  shareEmail: share_email_error || '',
                },
              });
            }
            if (response.status === 422 || response.status === 500) {
              this.setState({
                forbidden: true,
                isSubmitting: false,
                step: 'badRequestState',
                errors: { ...defaultErrors },
              });
            }
          });
      }
    );
  };

  handleAppsChange = (apps) => {
    this.setState({ selectedApps: apps });
  };

  handleForbiddenStatusChange = (forbidden) => {
    this.setState({ forbidden });
  };

  handleStepChange = (step, scrollable = true) => {
    this.setState({ step });
    if (scrollable) scrollToTop(step === 'appsSelection' ? 20 : 0);
  };

  handleReset = () => {
    if (this.state.redirectedFlow) dispatchSwitchAppFlowEvent({ appFlow: 'single' });

    this.setState({
      selectedApps: [],
      step: 'appsSelection',
      redirectedFlow: false,
    });
    scrollToTop(this.state.mobileMode ? -90 : 20);
    toggleSuccessUrlSearchParam('off', 'multisuccess');
  };

  handleSearchFocus = () => {
    scrollToTop(20);
  };

  render() {
    const {
      mobileMode,
      selectedApps,
      step,
      isSubmitting,
      forbidden,
      errors,
      skipUserStep,
      redirectedFlow,
      y,
    } = this.state;

    if (showReportUpdatedState) {
      return <RefreshRecommendations className="report-analysis-search-holder" />;
    }

    return (
      <div
        className={cx(
          'report-analysis-search-holder',
          {
            'report-analysis-search-admin': isAdminFlow,
            'cover-image': !isEmpty(coverImage) && step === 'appsSelection',
          },
          step
        )}
      >
        {step === 'appsSelection' && (
          <Fragment>
            {mobileMode && !isAdminFlow ? (
              <Fragment>
                {isMobileModalFlow ? (
                  <AppSelectionMobileModal
                    preSelectedApp={selectedApps}
                    selectionForbidden={forbidden}
                    topPopularApps={defaultSearchResults}
                    customTexts={customMobileFields}
                    coverImage={designType === 'with-report-cover' ? coverImage.mobile : ''}
                    redirectedFlow={redirectedFlow}
                    windowY={y}
                    onChange={this.handleAppsChange}
                    onNextStepClick={this.handleStepChange}
                    onForbiddenStatusChange={this.handleForbiddenStatusChange}
                  />
                ) : (
                  <AppsSelectionMobile
                    appsSelected={selectedApps}
                    preSelectedApp={preSelectedApp}
                    selectionForbidden={forbidden}
                    onChange={this.handleAppsChange}
                    onNextStepClick={this.handleStepChange}
                    onForbiddenStatusChange={this.handleForbiddenStatusChange}
                  />
                )}
              </Fragment>
            ) : (
              <AppsSelection
                preSelectedApp={selectedApps}
                defaultSearchResults={defaultSearchResults}
                selectionForbidden={forbidden}
                enableSmartSearch={isAdminFlow}
                customButton={
                  skipUserStep && (
                    <Button
                      className="report-analysis-submit-btn"
                      size="lg"
                      disabled={isSubmitting || selectedApps.length < 3}
                      onClick={() => this.handleFormSubmitClick()}
                    >
                      Submit
                    </Button>
                  )
                }
                coverImage={designType === 'with-report-cover' ? coverImage.desktop : ''}
                customTexts={customDesktopFields}
                redirectedFlow={redirectedFlow}
                onChange={this.handleAppsChange}
                onSearchFocus={this.handleSearchFocus}
                onNextStepClick={this.handleStepChange}
                onForbiddenStatusChange={this.handleForbiddenStatusChange}
              />
            )}
            {isAdminFlow && (
              <div className="bdr-generate-report-checkbox">
                <SimpleCheckbox
                  id="bdr-generate-report"
                  label="BDR generate report"
                  checked={!skipUserStep}
                  gap="8px"
                  onChange={() => this.setState({ skipUserStep: !skipUserStep })}
                />
              </div>
            )}
          </Fragment>
        )}

        {step === 'userReportForm' && !isMobileModalFlow && (
          <UserReportForm
            isSubmitting={isSubmitting}
            sitekey={sitekey}
            submitStarted={() => this.setState({ isSubmitting: true })}
            customErrors={errors}
            title={
              mobileMode ? 'Enter Your Information' : 'Enter your email to receive your report'
            }
            className={`report-analysis-output ${mobileMode && 'app-selection-mobile-container'}`}
            isMobileMode={mobileMode}
            isAdminFlow={isAdminFlow}
            env={env}
            onSubmitClick={this.handleFormSubmitClick}
            onStepBack={() => this.handleStepChange('appsSelection', false)}
          />
        )}

        <ReportFormWrapper
          show={step === 'userReportForm' && isMobileModalFlow}
          isMobileMode={mobileMode}
        >
          <UserReportFormMobileModal
            isSubmitting={isSubmitting}
            sitekey={sitekey}
            submitStarted={() => this.setState({ isSubmitting: true })}
            customErrors={errors}
            title={
              mobileMode ? 'Enter Your Information' : 'Enter your email to receive your report'
            }
            className="report-analysis-output"
            isMobileMode={mobileMode}
            windowY={y}
            onSubmitClick={this.handleFormSubmitClick}
            onStepBack={() => this.handleStepChange('appsSelection')}
          />
        </ReportFormWrapper>

        {step === 'showReferralState' && (
          <Referral
            isSubmitting={isSubmitting}
            sitekey={sitekey}
            submitStarted={() => this.setState({ isSubmitting: true })}
            customErrors={errors}
            isMobileMode={mobileMode}
            isMobileModalFlow={isMobileModalFlow}
            env={env}
            onSubmitClick={this.handleFormSubmitClick}
          />
        )}

        {step === 'reachedQuotaState' && (
          <ReachedQuotaState isMobileMode={mobileMode && !isMobileModalFlow} />
        )}

        {step === 'badRequestState' && (
          <BadResponseState
            error="Please, try again later."
            isMobileMode={mobileMode && !isMobileModalFlow}
          />
        )}
      </div>
    );
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const root = createRoot(rootNode);
  root.render(<AppSearch />);
});
